import { useEffect, useState } from "react"
import { useParams } from "react-router"
import type { ServerBackupEntry } from "@mc/types"
import { createBackup, deleteBackup, listBackups } from "../api/backups.js"
import { friendlyError } from "../api/errorMessages.js"
import { Spinner } from "../components/ui/Spinner.js"
import { Button } from "../components/ui/Button.js"
import { useToast } from "../components/ui/Toast.js"
import { Coachmark } from "../components/onboarding/Coachmark.js"
import { useFirstVisit } from "../components/onboarding/useFirstVisit.js"
import { getHint } from "../components/onboarding/hints.js"

type PageState =
  | { status: "loading" }
  | { status: "ready"; backups: ServerBackupEntry[] }
  | { status: "error"; message: string }

/**
 * FR seccion 8/19 item 11 (/servers/[id]/backups). Primer punto del
 * frontend que depende del mecanismo de peticion/respuesta sincronica
 * Gateway<->server-manager (ver services/gateway/src/routes/backups.ts):
 * a diferencia de start/stop/restart/kill (202 + WebSocket mas tarde),
 * listar/crear/borrar un backup necesita la respuesta dentro de esta
 * misma llamada, asi que no hay un estado "pendiente" que sobreviva un
 * refresh de pagina — solo loading local por accion, igual que
 * ConfigRow en AdminPage.tsx.
 *
 * Backup acá es el respaldo del mundo/servidor de Minecraft via el
 * backup manager nativo de Crafty, no el backup de la base de datos de
 * este proyecto (eso es packages/backup-storage, un concepto separado,
 * ver PR seccion 10.1).
 */
export function ServerBackupsPage() {
  const { serverId } = useParams<{ serverId: string }>()
  const [state, setState] = useState<PageState>({ status: "loading" })
  const [creating, setCreating] = useState(false)
  const [deletingKey, setDeletingKey] = useState<string | null>(null)
  const toast = useToast()
  const [isFirstVisit, dismissFirstVisit] = useFirstVisit("server-backups")

  useEffect(() => {
    if (serverId) load(serverId)
  }, [serverId])

  function load(id: string) {
    setState({ status: "loading" })
    listBackups(id)
      .then(({ backups }) => setState({ status: "ready", backups }))
      .catch((err) => setState({ status: "error", message: friendlyError(err).message }))
  }

  if (!serverId) return null

  if (state.status === "loading") {
    return (
      <div style={{ display: "grid", placeItems: "center", padding: "var(--space-12) 0" }}>
        <Spinner size="lg" />
      </div>
    )
  }

  if (state.status === "error") {
    return (
      <div style={{ display: "flex", flexDirection: "column", gap: "var(--space-3)" }}>
        <p role="alert" style={{ color: "var(--status-error)", fontSize: "var(--text-sm)" }}>
          {state.message}
        </p>
        <Button variant="secondary" size="sm" onClick={() => load(serverId)} style={{ alignSelf: "flex-start" }}>
          Reintentar
        </Button>
      </div>
    )
  }

  const { backups } = state

  async function handleCreate() {
    setCreating(true)
    try {
      await createBackup(serverId as string)
      toast.show("Backup creado.", "success")
      load(serverId as string)
    } catch (err) {
      toast.show(friendlyError(err).message, "error")
    } finally {
      setCreating(false)
    }
  }

  function handleDelete(backup: ServerBackupEntry) {
    // Design principle #3: 3000ms de ventana para arrepentirse antes de
    // que la accion destructiva se ejecute de verdad (mismo patron que
    // Detener/Forzar apagado en ServerOverviewPage). El backup sigue
    // visible en la lista local mientras dura la cuenta regresiva.
    toast.confirmDestructive(
      `Se va a eliminar el backup "${backup.key}".`,
      async () => {
        setDeletingKey(backup.key)
        try {
          await deleteBackup(serverId as string, backup.key)
          setState((prev) =>
            prev.status === "ready" ? { ...prev, backups: prev.backups.filter((b) => b.key !== backup.key) } : prev,
          )
          toast.show("Backup eliminado.", "success")
        } catch (err) {
          toast.show(friendlyError(err).message, "error")
        } finally {
          setDeletingKey(null)
        }
      },
      () => toast.show("Eliminación cancelada.", "info"),
    )
  }

  const listSection =
    backups.length === 0 ? (
      <div
        style={{
          padding: "var(--space-6)",
          borderRadius: "var(--radius-md)",
          border: "1px dashed var(--border-default)",
          color: "var(--text-muted)",
          fontSize: "var(--text-sm)",
          textAlign: "center",
        }}
      >
        Todavía no hay backups de este servidor.
      </div>
    ) : (
      <div style={{ display: "flex", flexDirection: "column", gap: "var(--space-3)" }}>
        {backups.map((backup) => (
          <BackupRow
            key={backup.key}
            backup={backup}
            deleting={deletingKey === backup.key}
            onDelete={() => handleDelete(backup)}
          />
        ))}
      </div>
    )

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "var(--space-6)" }}>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: "var(--space-4)",
          flexWrap: "wrap",
        }}
      >
        <div>
          <h1 style={{ fontSize: "var(--text-3xl)" }}>Backups</h1>
          <p style={{ color: "var(--text-muted)" }}>Respaldos del mundo y archivos del servidor.</p>
        </div>
        <Button loading={creating} onClick={handleCreate}>
          Crear backup ahora
        </Button>
      </div>

      {isFirstVisit ? (
        <Coachmark title={getHint("server-backups").title} description={getHint("server-backups").description} onDismiss={dismissFirstVisit}>
          {listSection}
        </Coachmark>
      ) : (
        listSection
      )}
    </div>
  )
}

function BackupRow({
  backup,
  deleting,
  onDelete,
}: {
  backup: ServerBackupEntry
  deleting: boolean
  onDelete: () => void
}) {
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: "var(--space-4)",
        flexWrap: "wrap",
        padding: "var(--space-4)",
        borderRadius: "var(--radius-md)",
        border: "1px solid var(--border-default)",
        background: "var(--bg-surface)",
        transition: `border-color var(--duration-small) var(--ease-out), opacity var(--duration-small) var(--ease-out)`,
        opacity: deleting ? 0.6 : 1,
      }}
    >
      <div style={{ display: "flex", flexDirection: "column", gap: "var(--space-1)" }}>
        <strong style={{ fontSize: "var(--text-sm)" }}>{backup.key}</strong>
        <span style={{ fontSize: "var(--text-xs)", color: "var(--text-muted)" }}>
          {new Date(backup.createdAt).toLocaleString()} · {formatBytes(backup.sizeBytes)}
        </span>
      </div>
      <Button variant="destructive" size="sm" loading={deleting} onClick={onDelete}>
        Eliminar
      </Button>
    </div>
  )
}

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`
  const units = ["KB", "MB", "GB", "TB"]
  let value = bytes / 1024
  let unitIndex = 0
  while (value >= 1024 && unitIndex < units.length - 1) {
    value /= 1024
    unitIndex += 1
  }
  return `${value.toFixed(1)} ${units[unitIndex]}`
}
